import { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../home/components/LoginModal';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import StylePreviewGallery from './components/StylePreviewGallery';
import AudioPreviewCard from './components/AudioPreviewCard';
import ShareExportPanel from './components/ShareExportPanel';
import CommunityVideos from './components/CommunityVideos';
import VideoPreviewModal, { VideoPreviewData } from './components/VideoPreviewModal';
import DraftsPanel, { DraftConfig } from './components/DraftsPanel';
import TrendingPromptsTicker from './components/TrendingPromptsTicker';
import VideoScriptEditor, { SceneItem, makeScene } from './components/VideoScriptEditor';
import GenerationQueue, { QueueItem } from './components/GenerationQueue';
import KeyboardShortcutsOverlay from './components/KeyboardShortcutsOverlay';
import RemixModal from './components/RemixModal';
import CollectionsPanel, { CollectionItem } from './components/CollectionsPanel';

interface AttachedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

interface VideoGeneration {
  id: string;
  prompt: string;
  videoType: string;
  duration: string;
  aspectRatio: string;
  style: string;
  resolution: string;
  audioOption: string;
  thumbnail: string;
  timestamp: number;
}

const VIDEO_TYPES = [
  { id: 'marketing', label: 'Marketing', icon: 'ri-megaphone-line' },
  { id: 'film', label: 'Film/Cinema', icon: 'ri-film-line' },
  { id: 'documentary', label: 'Documentary', icon: 'ri-book-open-line' },
  { id: 'social', label: 'Social Media', icon: 'ri-share-line' },
  { id: 'explainer', label: 'Explainer', icon: 'ri-lightbulb-line' },
  { id: 'animation', label: 'Animation', icon: 'ri-play-circle-line' },
];

const DURATIONS = ['15s', '30s', '60s', '2min', '5min'];
const STYLES = ['Realistic', 'Cinematic', 'Animated', 'Abstract'];
const RESOLUTIONS = ['720p', '1080p', '4K'];

const EXAMPLE_PROMPTS: { category: string; prompts: string[] }[] = [
  {
    category: 'Marketing',
    prompts: [
      'A luxury watch commercial featuring close-up shots of intricate mechanisms, elegant wrist movements, and sophisticated lifestyle scenes in a modern penthouse',
      'Tech startup product launch video showcasing innovative app features with dynamic UI animations and happy users in vibrant coworking spaces',
      'Eco-friendly fashion brand campaign with models in sustainable clothing walking through lush green forests and urban gardens'
    ]
  },
  {
    category: 'Short Film',
    prompts: [
      'A lone astronaut discovers an abandoned space station with mysterious artifacts, dramatic lighting, and suspenseful atmosphere',
      'Two strangers meet at a rainy train station, share an umbrella, and experience a brief but meaningful connection',
      'A time-traveler visits key moments in their past, trying to change one decision that altered their life forever'
    ]
  },
  {
    category: 'Documentary',
    prompts: [
      'Ocean conservation documentary showing coral reefs, marine life, plastic pollution, and scientists working on restoration projects',
      'Behind-the-scenes look at artisan coffee roasting, from bean selection to the perfect cup, with expert interviews',
      'Urban wildlife adaptation: how animals thrive in modern cities, featuring foxes, birds, and raccoons navigating city life'
    ]
  },
  {
    category: 'Social Media',
    prompts: [
      '15-second recipe reel: quick overhead shots of ingredients, fast-paced cooking steps, final plated dish reveal with trendy music',
      'Fitness transformation reel: before/after clips, workout montage, motivational text overlays, energetic background music',
      'Travel destination reel: stunning drone shots of tropical beaches, local culture, adventure activities, wanderlust vibes'
    ]
  }
];

const GENERATION_STEPS = [
  { label: 'Analyzing prompt', icon: 'ri-eye-line' },
  { label: 'Generating storyboard', icon: 'ri-layout-grid-line' },
  { label: 'Rendering frames', icon: 'ri-film-line' },
  { label: 'Applying style', icon: 'ri-brush-line' },
  { label: 'Adding audio', icon: 'ri-volume-up-line' },
  { label: 'Finalizing video', icon: 'ri-check-line' }
];

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_HISTORY_ITEMS = 20;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const ACCEPTED_DOC_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv'
];

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function AIVideo() {
  const navigate = useNavigate();
  const [darkMode, toggleDarkMode] = useDarkMode();
  const setDarkMode = (v: boolean) => { if (v !== darkMode) toggleDarkMode(); };

  const [selectedType, setSelectedType] = useState('marketing');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState('30s');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [style, setStyle] = useState('Cinematic');
  const [resolution, setResolution] = useState('1080p');
  const [audioOption, setAudioOption] = useState('Background Music');
  const [includeAudio, setIncludeAudio] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<VideoGeneration[]>([]);
  const [activeExampleCategory, setActiveExampleCategory] = useState('Marketing');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<VideoPreviewData | null>(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [activeInputTab, setActiveInputTab] = useState<'prompt' | 'script'>('prompt');
  const [scenes, setScenes] = useState<SceneItem[]>(() => [makeScene()]);
  const [showQueue, setShowQueue] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [pendingCollectionItem, setPendingCollectionItem] = useState<CollectionItem | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('ai-video-history');
      if (savedHistory) setHistory(JSON.parse(savedHistory));
    } catch (e) {
      console.error('Failed to load history:', e);
    }
  }, []);

  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('ai-video-history', JSON.stringify(history));
    }
  }, [history]);

  const saveToHistory = (thumbnail: string) => {
    const newGeneration: VideoGeneration = {
      id: `${Date.now()}-${Math.random()}`,
      prompt,
      videoType: selectedType,
      duration,
      aspectRatio,
      style,
      resolution,
      audioOption: includeAudio ? audioOption : 'No Audio',
      thumbnail,
      timestamp: Date.now(),
    };
    setHistory(prev => [newGeneration, ...prev].slice(0, MAX_HISTORY_ITEMS));
  };

  const restoreFromHistory = (generation: VideoGeneration) => {
    setPrompt(generation.prompt);
    setSelectedType(generation.videoType);
    setDuration(generation.duration);
    setAspectRatio(generation.aspectRatio);
    setStyle(generation.style);
    setResolution(generation.resolution);
    setAudioOption(generation.audioOption);
    setShowHistory(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeFromHistory = (id: string) => {
    setHistory(prev => prev.filter(item => item.id !== id));
  };

  const clearAllHistory = () => {
    setHistory([]);
    localStorage.removeItem('ai-video-history');
  };

  /* ---------- File handling ---------- */
  const handleFiles = (files: FileList) => {
    setUploadError('');
    const newFiles: AttachedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (attachments.length + newFiles.length >= MAX_FILES) {
        setUploadError(`Maximum ${MAX_FILES} files allowed`);
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`${file.name} exceeds 25MB limit`);
        continue;
      }
      const allAccepted = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_DOC_TYPES];
      if (!allAccepted.includes(file.type)) {
        setUploadError(`${file.name} is not a supported file type`);
        continue;
      }
      const fileObj: AttachedFile = {
        id: `${Date.now()}-${i}`,
        name: file.name,
        size: file.size,
        type: file.type,
      };
      if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachments(prev =>
            prev.map(f => f.id === fileObj.id ? { ...f, preview: e.target?.result as string } : f)
          );
        };
        reader.readAsDataURL(file);
      }
      newFiles.push(fileObj);
    }
    setAttachments(prev => [...prev, ...newFiles]);
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(f => f.id !== id));
    setUploadError('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
  };

  /* ---------- Prompt enhancement ---------- */
  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing) return;
    setIsEnhancing(true);
    await new Promise(r => setTimeout(r, 1600));

    const lightingOptions = ['golden-hour warmth', 'dramatic side lighting', 'soft diffused daylight', 'moody neon glow'];
    const cameraOptions = ['slow dolly push-in', 'sweeping crane shot', 'handheld tracking movement', 'smooth parallax drift'];
    const gradeOptions = ['warm amber and ivory', 'cool desaturated cinematic', 'vibrant high-contrast', 'teal-and-orange Hollywood'];
    const cutOptions = ['elegant dissolve', 'kinetic whip-pan', 'precise match cut', 'gentle cross-fade'];
    const moodOptions = ['immersive and emotionally resonant', 'dynamic and high-energy', 'intimate and introspective', 'dramatic and intense'];

    const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

    const enhanced =
      `Opening with a sweeping cinematic establishing shot, ${prompt.trim()} — ` +
      `bathed in ${pick(lightingOptions)}, captured with a ${pick(cameraOptions)}. ` +
      `Rich depth of field and carefully composed frames guide the eye throughout. ` +
      `Color grading evokes a ${pick(gradeOptions)} palette. ` +
      `Scenes transition via ${pick(cutOptions)} cuts for a polished, professional finish. ` +
      `The overall atmosphere is ${pick(moodOptions)}.`;

    setPrompt(enhanced.slice(0, 1000));
    setIsEnhancing(false);
  };

  /* ---------- Generation ---------- */
  const startGeneration = () => {
    setIsGenerating(true);
    setCurrentStep(0);
    setGeneratedVideo(null);

    GENERATION_STEPS.forEach((_, index) => {
      setTimeout(() => {
        setCurrentStep(index + 1);
        if (index === GENERATION_STEPS.length - 1) {
          setTimeout(() => {
            setIsGenerating(false);
            const videoUrl = 'https://public.readdy.ai/ai/img_res/86ea4c983708b012bab48ccc89851773.jpg';
            setGeneratedVideo(videoUrl);
            saveToHistory(videoUrl);
          }, 500);
        }
      }, 1500 * (index + 1));
    });
  };

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    startGeneration();
  };

  const handleRemix = (remixPrompt: string) => {
    setPrompt(remixPrompt);
    setShowSharePanel(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRestoreDraft = (config: DraftConfig) => {
    setPrompt(config.prompt);
    setSelectedType(config.selectedType);
    setDuration(config.duration);
    setAspectRatio(config.aspectRatio);
    setStyle(config.style);
    setResolution(config.resolution);
    setAudioOption(config.audioOption);
    setShowDrafts(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerateFromScript = (stitchedPrompt: string) => {
    if (!stitchedPrompt.trim()) return;
    setPrompt(stitchedPrompt);
    setActiveInputTab('prompt');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // brief delay so the textarea is visible before generation starts
    setTimeout(() => startGeneration(), 300);
  };

  const handleAddToQueue = () => {
    if (!prompt.trim()) return;
    const newItem: QueueItem = {
      id: `queue-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      prompt,
      selectedType,
      duration,
      aspectRatio,
      style,
      resolution,
      audioOption,
      status: 'pending',
      addedAt: Date.now(),
      label: prompt.length <= 50 ? prompt : `${prompt.slice(0, 50)}…`,
      progress: 0,
    };
    setQueueItems(prev => [...prev, newItem]);
    setShowQueue(true);
  };

  const handleSaveToCollection = () => {
    if (!generatedVideo) return;
    const item: CollectionItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      thumbnail: generatedVideo,
      prompt,
      style,
      duration,
      resolution,
      savedAt: Date.now(),
    };
    setPendingCollectionItem(item);
    setShowCollections(true);
  };

  /* ---------- Global keyboard shortcuts ---------- */
  useEffect(() => {
    const shouldIgnore = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    };

    const handler = (e: KeyboardEvent) => {
      if (shouldIgnore(e)) return;
      switch (e.key) {
        case '?':
          e.preventDefault();
          setShowShortcuts(s => !s);
          break;
        case 'Escape':
          setShowShortcuts(false);
          setShowDrafts(false);
          setShowHistory(false);
          setShowQueue(false);
          setShowRemixModal(false);
          setShowCollections(false);
          break;
        case 'd':
        case 'D':
          e.preventDefault();
          setShowDrafts(s => !s);
          break;
        case 'h':
        case 'H':
          e.preventDefault();
          setShowHistory(s => !s);
          break;
        case 'q':
          if (e.shiftKey) { e.preventDefault(); setShowQueue(s => !s); }
          else { e.preventDefault(); if (prompt.trim()) handleAddToQueue(); }
          break;
        case 'Q':
          if (e.shiftKey) { e.preventDefault(); setShowQueue(s => !s); }
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          setActiveInputTab('prompt');
          break;
        case 's':
        case 'S':
          if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); setActiveInputTab('script'); }
          break;
        case 'e':
        case 'E':
          if (activeInputTab === 'prompt' && prompt.trim() && !isEnhancing) {
            e.preventDefault();
            handleEnhancePrompt();
          }
          break;
        case 'g':
        case 'G':
          if (activeInputTab === 'prompt' && prompt.trim() && !isGenerating) {
            e.preventDefault();
            handleGenerate();
          }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, activeInputTab, isEnhancing, isGenerating]);

  const truncateText = (text: string, max: number) =>
    text.length <= max ? text : `${text.slice(0, max)}...`;

  /* ---------- Render ---------- */
  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        darkMode 
          ? 'bg-gray-900/80 backdrop-blur-md border-b border-white/10' 
          : 'bg-white/80 backdrop-blur-md border-b border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="cursor-pointer flex-shrink-0">
              <div className="bg-white rounded-xl px-2 py-1">
                <img
                  src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg"
                  alt="1WORLD-AI Logo"
                  className="h-11 w-auto object-contain"
                />
              </div>
            </button>
            <div className="flex items-center gap-3">
              <ToolsDropdown isDarkMode={darkMode} />
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <i className={`${darkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl text-gray-700 dark:text-gray-300`}></i>
              </button>
              <button
                onClick={() => setShowDrafts(!showDrafts)}
                className="relative px-4 py-2.5 rounded-lg border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all whitespace-nowrap text-sm flex items-center gap-2"
              >
                <i className="ri-draft-line text-base"></i>
                My Drafts
              </button>
              <button
                onClick={() => setShowQueue(!showQueue)}
                className="relative px-4 py-2.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-all whitespace-nowrap text-sm flex items-center gap-2"
              >
                <i className="ri-stack-line text-base"></i>
                Queue
                {queueItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {queueItems.filter(i => i.status === 'pending' || i.status === 'generating').length || queueItems.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowCollections(!showCollections)}
                className="relative px-4 py-2.5 rounded-lg border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all whitespace-nowrap text-sm flex items-center gap-2"
              >
                <i className="ri-folder-3-line text-base"></i>
                Collections
              </button>
              <button
                onClick={() => setShowShortcuts(true)}
                title="Keyboard shortcuts (?)"
                className="p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <i className="ri-keyboard-line text-xl text-gray-700 dark:text-gray-300"></i>
              </button>
              <button
                onClick={() => setShowLoginModal(true)}
                className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-all whitespace-nowrap text-sm flex items-center gap-2"
              >
                <i className="ri-user-line text-base"></i>
                Sign In
              </button>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="relative px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-600 text-white font-medium hover:shadow-lg hover:shadow-red-500/30 transition-all whitespace-nowrap"
              >
                <i className="ri-history-line mr-2"></i>
                My Generations
                {history.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
                    {history.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="fixed top-16 left-0 right-0 z-40">
        <Breadcrumb currentPage="AI Video" variant={darkMode ? 'dark' : 'light'} />
      </div>

      {/* Main Content */}
      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-sm font-medium mb-6">
              <div className="relative">
                <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-orange-600 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-2 h-2 bg-gradient-to-r from-red-500 to-orange-600 rounded-full animate-ping opacity-75"></div>
              </div>
              <span className="font-semibold">OpenClaw Creative AI</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold mb-6 ml-3">
              <i className="ri-checkbox-circle-fill"></i>
              <span>8M+ Videos Generated</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Create Stunning Videos
              <span className="block bg-gradient-to-r from-red-500 to-orange-600 bg-clip-text text-transparent">
                With AI Magic
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Generate professional videos powered by OpenClaw Creative AI engine
            </p>
          </div>

          {/* Trending Prompts Ticker */}
          <div className="mb-10">
            <TrendingPromptsTicker
              onSelectPrompt={(p) => { setPrompt(p); setActiveInputTab('prompt'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              darkMode={darkMode}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">

              {/* Input Mode Tab Switcher */}
              <div className={`flex rounded-xl p-1 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setActiveInputTab('prompt')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                    activeInputTab === 'prompt'
                      ? 'bg-gradient-to-r from-red-500 to-orange-600 text-white'
                      : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-edit-line text-base"></i>
                  Prompt
                </button>
                <button
                  onClick={() => setActiveInputTab('script')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                    activeInputTab === 'script'
                      ? 'bg-gradient-to-r from-red-500 to-orange-600 text-white'
                      : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-file-list-3-line text-base"></i>
                  Video Script
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    activeInputTab === 'script'
                      ? 'bg-white/20 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                  }`}>New</span>
                </button>
              </div>

              {/* Prompt Input — prompt tab only */}
              {activeInputTab === 'prompt' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  <i className="ri-edit-line mr-2 text-red-500"></i>
                  Describe Your Video
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value.slice(0, 1000))}
                  placeholder="Describe the video you want to create in detail. Include scenes, mood, actions, camera angles, and any specific elements..."
                  className="w-full h-32 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-3 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                />
                <div className="flex items-center justify-between mt-3 gap-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{prompt.length} / 1000</span>
                  <div className="flex items-center gap-2">
                    {prompt.trim().length > 0 && (
                      <button
                        onClick={handleEnhancePrompt}
                        disabled={isEnhancing}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                          isEnhancing
                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-amber-400 to-orange-400 text-white hover:from-amber-500 hover:to-orange-500 hover:shadow-md'
                        }`}
                        title="AI expands your prompt with cinematic detail"
                      >
                        {isEnhancing ? (
                          <><i className="ri-loader-4-line animate-spin text-sm"></i>Enhancing…</>
                        ) : (
                          <><i className="ri-magic-line text-sm"></i>Enhance Prompt</>
                        )}
                      </button>
                    )}
                    {prompt.length > 0 && (
                      <button
                        onClick={() => setPrompt('')}
                        className="text-xs text-red-500 dark:text-red-400 hover:underline font-medium whitespace-nowrap"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* Video Script Editor — script tab only */}
              {activeInputTab === 'script' && (
                <VideoScriptEditor
                  scenes={scenes}
                  onScenesChange={setScenes}
                  onGenerateFromScript={handleGenerateFromScript}
                  darkMode={darkMode}
                />
              )}

              {/* Video Type */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  <i className="ri-movie-line mr-2 text-red-500"></i>
                  Video Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {VIDEO_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        selectedType === type.id
                          ? 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-red-300 dark:hover:border-red-600'
                      }`}
                    >
                      <i className={`${type.icon} text-lg text-red-500 dark:text-red-400 mb-1 block`}></i>
                      <span className={`text-sm font-medium ${
                        selectedType === type.id ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-white"
                >
                  <span>
                    <i className="ri-settings-3-line mr-2 text-red-500"></i>
                    Advanced Settings
                  </span>
                  <i className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line text-gray-400`}></i>
                </button>

                {showAdvanced && (
                  <div className="mt-4 space-y-4">
                    {/* Duration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration</label>
                      <div className="flex gap-2 flex-wrap">
                        {DURATIONS.map((dur) => (
                          <button
                            key={dur}
                            onClick={() => setDuration(dur)}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap ${
                              duration === dur
                                ? 'bg-red-500 dark:bg-red-600 text-white border-red-500'
                                : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {dur}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Aspect Ratio</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: '16:9', label: 'Landscape' },
                          { id: '9:16', label: 'Portrait' },
                          { id: '1:1', label: 'Square' },
                        ].map((ratio) => (
                          <button
                            key={ratio.id}
                            onClick={() => setAspectRatio(ratio.id)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              aspectRatio === ratio.id
                                ? 'bg-red-500 dark:bg-red-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {ratio.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Style */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Visual Style</label>
                      <div className="grid grid-cols-2 gap-2">
                        {STYLES.map((s) => (
                          <button
                            key={s}
                            onClick={() => setStyle(s)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              style === s
                                ? 'bg-red-500 dark:bg-red-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Resolution */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resolution</label>
                      <div className="grid grid-cols-3 gap-2">
                        {RESOLUTIONS.map((res) => (
                          <button
                            key={res}
                            onClick={() => setResolution(res)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              resolution === res
                                ? 'bg-red-500 dark:bg-red-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {res}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Audio */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative">
                          <input
                            type="checkbox"
                            id="includeAudio"
                            checked={includeAudio}
                            onChange={(e) => setIncludeAudio(e.target.checked)}
                            className="opacity-0 absolute w-4 h-4 cursor-pointer"
                          />
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                              includeAudio ? 'bg-red-500 border-red-500' : 'border-gray-400 dark:border-gray-600'
                            }`}
                          >
                            {includeAudio && <i className="ri-check-line text-white text-xs"></i>}
                          </div>
                        </div>
                        <label htmlFor="includeAudio" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                          Include AI-generated audio
                        </label>
                      </div>
                      {includeAudio && (
                        <div className="grid grid-cols-2 gap-2">
                          {['Background Music', 'Voiceover', 'Sound Effects', 'No Audio'].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => setAudioOption(opt)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                audioOption === opt
                                  ? 'bg-red-500 dark:bg-red-600 text-white'
                                  : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Reference Files */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAttachments(!showAttachments)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-white"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-attachment-line text-red-500"></i>
                    Reference Files
                    {attachments.length > 0 && (
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs rounded-full">
                        {attachments.length}
                      </span>
                    )}
                  </span>
                  <i className={`ri-arrow-${showAttachments ? 'up' : 'down'}-s-line text-gray-400`}></i>
                </button>

                {showAttachments && (
                  <div className="mt-4 space-y-4">
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        isDragging
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 hover:border-red-400'
                      }`}
                    >
                      <i className="ri-upload-cloud-line text-4xl text-red-500 mb-3 block"></i>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Drop files here or click to browse</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Images, videos, or documents (max 25MB each)</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploadError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-600 dark:text-red-400">
                          <i className="ri-error-warning-line mr-1"></i>{uploadError}
                        </p>
                      </div>
                    )}
                    {attachments.length > 0 && (
                      <div className="space-y-3">
                        {attachments.filter(f => f.type.startsWith('image/')).length > 0 && (
                          <div className="grid grid-cols-4 gap-2">
                            {attachments.filter(f => f.type.startsWith('image/')).map((file) => (
                              <div key={file.id} className="relative group aspect-square">
                                {file.preview && (
                                  <img src={file.preview} alt={file.name} className="w-full h-full object-cover rounded-lg" />
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); removeAttachment(file.id); }}
                                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                >
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {attachments.filter(f => !f.type.startsWith('image/')).map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-900 rounded-lg">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <i className={`${file.type.startsWith('video/') ? 'ri-video-line' : 'ri-file-text-line'} text-xl text-red-500`}></i>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                            </div>
                            <button onClick={() => removeAttachment(file.id)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                              <i className="ri-delete-bin-6-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Generate Button — prompt tab only */}
              {activeInputTab === 'prompt' && <div className="flex gap-2">
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className="flex-1 py-4 bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                >
                  {isGenerating ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Generating Video...</>
                  ) : (
                    <><i className="ri-video-line mr-2"></i>Generate Video</>
                  )}
                </button>
                <button
                  onClick={handleAddToQueue}
                  disabled={!prompt.trim()}
                  title="Add to Queue (Q)"
                  className="px-4 py-4 rounded-xl border-2 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-stack-line text-lg"></i>
                  <span className="hidden sm:inline text-sm">Queue</span>
                </button>
              </div>}

              {/* Example Prompts — prompt tab only */}
              {activeInputTab === 'prompt' && <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  <i className="ri-lightbulb-line mr-2 text-red-500"></i>
                  Example Prompts
                </h3>
                <div className="flex gap-2 flex-wrap mb-4">
                  {EXAMPLE_PROMPTS.map((cat) => (
                    <button
                      key={cat.category}
                      onClick={() => setActiveExampleCategory(cat.category)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        activeExampleCategory === cat.category
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/20'
                      }`}
                    >
                      {cat.category}
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  {EXAMPLE_PROMPTS.find(c => c.category === activeExampleCategory)?.prompts.map((ep, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(ep)}
                      className="w-full text-left p-3 bg-gray-50 dark:bg-gray-900 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 transition-all border border-transparent hover:border-red-200 dark:hover:border-red-800"
                    >
                      {truncateText(ep, 100)}
                    </button>
                  ))}
                </div>
              </div>}
            </div>

            {/* Right Column - Result */}
            <div className="space-y-6">
              {isGenerating ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <i className="ri-video-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Creating Your Video</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {GENERATION_STEPS[Math.min(currentStep, GENERATION_STEPS.length - 1)]?.label}
                    </p>
                  </div>
                  <div className="space-y-3">
                    {GENERATION_STEPS.map((step, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          i < currentStep ? 'bg-green-500' : i === currentStep ? 'bg-red-500 animate-pulse' : 'bg-gray-200 dark:bg-gray-700'
                        }`}>
                          {i < currentStep
                            ? <i className="ri-check-line text-white text-xs"></i>
                            : i === currentStep
                            ? <i className="ri-loader-4-line text-white text-xs animate-spin"></i>
                            : <span className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full"></span>
                          }
                        </div>
                        <span className={`text-sm ${i <= currentStep ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400 dark:text-gray-600'}`}>
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-red-500 to-orange-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(currentStep / GENERATION_STEPS.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ) : generatedVideo ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <img
                      src={generatedVideo}
                      alt="Generated video thumbnail"
                      className="w-full object-cover cursor-pointer"
                      style={{ aspectRatio: '16/9' }}
                      onClick={() => setPreviewVideo({
                        id: `generated-${Date.now()}`,
                        thumbnail: generatedVideo,
                        prompt,
                        style,
                        duration,
                        resolution,
                      })}
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer hover:bg-black/40 transition-colors"
                      onClick={() => setPreviewVideo({
                        id: `generated-${Date.now()}`,
                        thumbnail: generatedVideo,
                        prompt,
                        style,
                        duration,
                        resolution,
                      })}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors">
                          <i className="ri-play-fill text-red-500 text-3xl ml-1"></i>
                        </div>
                        <span className="text-white text-xs font-semibold tracking-wide">Click to Preview</span>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 flex gap-2">
                      <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{style}</span>
                      <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{resolution}</span>
                      <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{duration}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{prompt}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowLoginModal(true)}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-orange-600 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-red-500/30 transition-all whitespace-nowrap"
                      >
                        <i className="ri-download-line mr-2"></i>Download
                      </button>
                      <button
                        onClick={() => setShowLoginModal(true)}
                        className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all whitespace-nowrap"
                      >
                        <i className="ri-share-line mr-2"></i>Share
                      </button>
                    </div>
                    {/* Remix + Save to Collection row */}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setShowRemixModal(true)}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-violet-500/30 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <i className="ri-refresh-line"></i>Remix
                      </button>
                      <button
                        onClick={handleSaveToCollection}
                        className="flex-1 px-4 py-2.5 border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 font-medium rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <i className="ri-folder-add-line"></i>Save
                      </button>
                    </div>
                    <button
                      onClick={startGeneration}
                      className="w-full mt-2 px-4 py-2.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                    >
                      <i className="ri-refresh-line mr-2"></i>Regenerate
                    </button>
                    <button
                      onClick={() => setShowSharePanel(prev => !prev)}
                      className={`w-full mt-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                        showSharePanel
                          ? darkMode ? 'bg-red-900/30 text-red-400 border border-red-700' : 'bg-red-50 text-red-600 border border-red-200'
                          : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <i className={`${showSharePanel ? 'ri-arrow-up-s-line' : 'ri-share-forward-line'} text-base`}></i>
                      {showSharePanel ? 'Hide Share Options' : 'Share & Export'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center border border-gray-200 dark:border-gray-700">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-video-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Your Video Will Appear Here</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Enter a prompt and click generate to create stunning AI videos</p>
                </div>
              )}

              {/* Share & Export panel (visible after generation) */}
              {generatedVideo && showSharePanel && (
                <ShareExportPanel
                  prompt={prompt}
                  style={style}
                  duration={duration}
                  resolution={resolution}
                  thumbnail={generatedVideo}
                  darkMode={darkMode}
                  onLoginRequired={() => setShowLoginModal(true)}
                />
              )}

              {/* Style Preview Gallery */}
              <StylePreviewGallery
                selectedStyle={style}
                onSelectStyle={setStyle}
                darkMode={darkMode}
              />

              {/* Audio Preview Card */}
              <AudioPreviewCard
                selectedAudio={audioOption}
                onSelectAudio={(opt) => { setAudioOption(opt); setIncludeAudio(true); }}
                darkMode={darkMode}
              />
            </div>
          </div>

          {/* Community Videos - full width below grid */}
          <div className="mt-12">
            <CommunityVideos
              darkMode={darkMode}
              onRemix={handleRemix}
              onPreview={setPreviewVideo}
            />
          </div>
        </div>

        {/* History Panel */}
        {showHistory && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowHistory(false)}></div>
            <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl z-50 overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-red-500 to-orange-600 text-white p-6 z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">My Generations</h3>
                  <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <p className="text-sm text-white/80">{history.length} of {MAX_HISTORY_ITEMS} saved</p>
              </div>
              <div className="p-6">
                {history.length > 0 ? (
                  <>
                    <button
                      onClick={clearAllHistory}
                      className="w-full mb-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                    >
                      <i className="ri-delete-bin-line mr-2"></i>Clear All History
                    </button>
                    <div className="space-y-4">
                      {history.map((item) => (
                        <div
                          key={item.id}
                          className="bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-600 transition-colors"
                        >
                          <div className="relative group">
                            <img src={item.thumbnail} alt={item.prompt} className="w-full h-40 object-cover" />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => restoreFromHistory(item)}
                                className="p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-100 transition-colors"
                                title="Restore settings"
                              >
                                <i className="ri-refresh-line"></i>
                              </button>
                              <button
                                onClick={() => removeFromHistory(item.id)}
                                className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                title="Remove"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-900 dark:text-white mb-3 line-clamp-2">{item.prompt}</p>
                            <div className="flex flex-wrap gap-1 mb-2">
                              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs rounded-full">
                                {VIDEO_TYPES.find(t => t.id === item.videoType)?.label || item.videoType}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.duration}</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.aspectRatio}</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.style}</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.resolution}</span>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(item.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-history-line text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No generations yet. Create your first video to see it here!</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <DraftsPanel
          isOpen={showDrafts}
          onClose={() => setShowDrafts(false)}
          currentConfig={{ prompt, selectedType, duration, aspectRatio, style, resolution, audioOption }}
          onRestore={handleRestoreDraft}
          darkMode={darkMode}
        />

        {previewVideo && (
          <VideoPreviewModal
            video={previewVideo}
            darkMode={darkMode}
            onClose={() => setPreviewVideo(null)}
            onRemix={previewVideo.author ? handleRemix : undefined}
          />
        )}

        <GenerationQueue
          isOpen={showQueue}
          onClose={() => setShowQueue(false)}
          items={queueItems}
          onItemsChange={setQueueItems}
          darkMode={darkMode}
        />

        <KeyboardShortcutsOverlay
          isOpen={showShortcuts}
          onClose={() => setShowShortcuts(false)}
          darkMode={darkMode}
        />

        {showRemixModal && generatedVideo && (
          <RemixModal
            originalPrompt={prompt}
            style={style}
            duration={duration}
            resolution={resolution}
            darkMode={darkMode}
            onClose={() => setShowRemixModal(false)}
            onRemix={(newPrompt) => {
              setPrompt(newPrompt);
              setActiveInputTab('prompt');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => startGeneration(), 300);
            }}
          />
        )}

        <CollectionsPanel
          isOpen={showCollections}
          onClose={() => setShowCollections(false)}
          darkMode={darkMode}
          pendingItem={pendingCollectionItem}
          onClearPending={() => setPendingCollectionItem(null)}
        />

        {showLoginModal && (
          <LoginModal onClose={() => setShowLoginModal(false)} />
        )}
      </div>
    </div>
  );
}
